import {
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  DialogActions,
  Button,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import omit from "lodash-es/omit";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import {
  CreateBigQueryTenantExportConfigInput,
  CreateBigQueryTenantExportConfigMutation,
  CreateBigQueryTenantExportConfigMutationVariables,
  DeleteBigQueryTenantExportConfigMutation,
  DeleteBigQueryTenantExportConfigMutationVariables,
  GetTenantExportConfigsQuery,
  TrackedJobStatus,
  UpdateBigQueryTenantExportConfigInput,
  UpdateBigQueryTenantExportConfigMutation,
  UpdateBigQueryTenantExportConfigMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import {
  CREATE_BIGQUERY_TENANT_EXPORT_CONFIG,
  DELETE_BIGQUERY_TENANT_EXPORT_CONFIG,
  UPDATE_BIGQUERY_TENANT_EXPORT_CONFIG,
} from "~frontend/graphql/tenantExportConfigs";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { Exact } from "~frontend/util/types";
import useMutation from "~frontend/util/useMutation";

type ExportConfig =
  GetTenantExportConfigsQuery["tenantExportConfigs"]["bigqueryExportConfig"];

type FormValues = {
  [submitErrorField]: string | null;
  gcsBucket: string;
  gcsBucketPrefix: string;
  dataset: string;
  credentialsJSON: string;
  location: string;
};

const BIGQUERY_LOCATION_OPTIONS = [
  { label: "US - Data centers in the United States", value: "US" },
  {
    label: "EU - Data centers within member states of the European Union",
    value: "EU",
  },
  { label: "us-east1 - South Carolina", value: "us-east1" },
  { label: "us-east4 - Northern Virginia", value: "us-east4" },
  { label: "us-east5 - Columbus, Ohio", value: "us-east5" },
  { label: "us-central1 - Iowa", value: "us-central1" },
  { label: "us-south1 - Dallas", value: "us-south1" },
  { label: "us-west1 - Oregon", value: "us-west1" },
  { label: "us-west2 - Los Angeles", value: "us-west2" },
  { label: "us-west3 - Salt Lake City", value: "us-west3" },
  { label: "us-west4 - Las Vegas", value: "us-west4" },
  {
    label: "northamerica-northeast1 - Montréal",
    value: "northamerica-northeast1",
  },
  {
    label: "northamerica-northeast2 - Toronto",
    value: "northamerica-northeast2",
  },
  { label: "asia-east1 - Taiwan", value: "asia-east1" },
  { label: "asia-east2 - Hong Kong", value: "asia-east2" },
  { label: "asia-northeast1 - Tokyo", value: "asia-northeast1" },
  { label: "asia-northeast2 - Osaka", value: "asia-northeast2" },
  { label: "asia-northeast3 - Seoul", value: "asia-northeast3" },
  { label: "asia-south1 - Mumbai", value: "asia-south1" },
  { label: "asia-south2 - Delhi", value: "asia-south2" },
  { label: "asia-southeast1 - Singapore", value: "asia-southeast1" },
  { label: "asia-southeast2 - Jakarta", value: "asia-southeast2" },
  { label: "australia-southeast1 - Sydney", value: "australia-southeast1" },
  { label: "australia-southeast2 - Melbourne", value: "australia-southeast2" },
  { label: "europe-central2 - Warsaw", value: "europe-central2" },
  { label: "europe-north1 - Finland", value: "europe-north1" },
  { label: "europe-southwest1 - Madrid", value: "europe-southwest1" },
  { label: "europe-west1 - Belgium", value: "europe-west1" },
  { label: "europe-west12 - Turin", value: "europe-west12" },
  { label: "europe-west2 - London", value: "europe-west2" },
  { label: "europe-west3 - Frankfurt", value: "europe-west3" },
  { label: "europe-west4 - Netherlands", value: "europe-west4" },
  { label: "europe-west6 - Zürich", value: "europe-west6" },
  { label: "europe-west8 - Milan", value: "europe-west8" },
  { label: "europe-west9 - Paris", value: "europe-west9" },
  { label: "me-central1 - Doha", value: "me-central1" },
  { label: "me-central2 - Dammam", value: "me-central2" },
  { label: "me-west1 - Tel Aviv", value: "me-west1" },
  { label: "southamerica-east1 - São Paulo", value: "southamerica-east1" },
  { label: "southamerica-west1 - Santiago", value: "southamerica-west1" },
];

function BigQueryExportConfigDialog({
  onClose,
  exportConfig,
  handleSubmitCreate,
  handleSubmitUpdate,
}: {
  onClose: () => void;
  exportConfig: ExportConfig;
  handleSubmitCreate: <T>(
    settings: Exact<T, CreateBigQueryTenantExportConfigInput>
  ) => Promise<void>;
  handleSubmitUpdate: <T>(
    settings: Exact<T, UpdateBigQueryTenantExportConfigInput>
  ) => Promise<void>;
}) {
  const isUpdate = !!exportConfig;

  const schema = yup.object().shape({
    gcsBucket: yup.string().required("Required"),
    gcsBucketPrefix: yup.string(),
    location: yup
      .string()
      .required("Required")
      .oneOf(BIGQUERY_LOCATION_OPTIONS.map(o => o.value)),
    dataset: yup.string().required("Required"),
    credentialsJSON: isUpdate
      ? yup.string()
      : yup.string().required("Required"),
  });

  const initialValues: FormValues = {
    [submitErrorField]: null,
    gcsBucket: exportConfig?.settings.gcsBucket ?? "",
    gcsBucketPrefix: exportConfig?.settings.gcsBucketPrefix ?? "",
    location: exportConfig?.settings.location ?? "US",
    dataset: exportConfig?.settings.dataset ?? "",
    credentialsJSON: "",
  };

  const handleSubmit = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    try {
      if (isUpdate) {
        await handleSubmitUpdate({
          ...omit(values, [submitErrorField]),
          credentialsJSON:
            values.credentialsJSON.trim().length > 0
              ? values.credentialsJSON
              : undefined,
        });
      } else {
        await handleSubmitCreate(omit(values, [submitErrorField]));
      }
    } catch (e: any) {
      formik.setErrors({
        [submitErrorField]: e.message,
      });
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>
        {isUpdate
          ? "Edit BigQuery Export Configuration"
          : "Configure BigQuery Export"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner noScrollOnError />
              <FormikTextField fullWidth name="gcsBucket" label="GCS Bucket" />
              <FormikTextField
                fullWidth
                name="gcsBucketPrefix"
                label="GCS Bucket Object Prefix (optional)"
              />
              <FormikTextField
                fullWidth
                name="dataset"
                label="BigQuery Dataset Name"
              />
              <FormikSelect fullWidth name="location" label="BigQuery Location">
                {BIGQUERY_LOCATION_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </FormikSelect>
              <FormikTextField
                fullWidth
                multiline
                name="credentialsJSON"
                label={
                  isUpdate
                    ? "Credentials JSON (leave blank to keep current)"
                    : "Credentials JSON"
                }
                inputProps={{
                  // blur text: https://stackoverflow.com/a/66618206
                  style: {
                    color: "transparent",
                    textShadow: "0 0 8px rgba(0,0,0,0.5)",
                  },
                }}
                rows={4}
              />
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function BigQueryExportDeleteButton({
  onDelete,
}: {
  onDelete: () => Promise<any>;
}): ReactElement {
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();

  return (
    <Button
      disabled={loading}
      color="secondary"
      onClick={async () => {
        const { confirmed } = await confirm({
          title: "Disable BigQuery Export?",
          description:
            "Are you sure you want to disable BigQuery exports? Any data in BigQuery will remain there, but no new exports will be run. You'll need to re-enter your AWS credentials to re-enable exports.",
        });
        if (!confirmed) {
          return;
        }

        setLoading(true);
        await onDelete();
        setLoading(false);
      }}
    >
      {loading ? "Disabling..." : "Disable"}
    </Button>
  );
}

export default function BigQueryTenantExportConfig({
  exportConfig,
}: {
  exportConfig: ExportConfig | undefined;
}): ReactElement {
  const navigate = useNavigate();
  const getErrorMessage = useErrorMessageFormatter();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [createExportConfig] = useMutation<
    CreateBigQueryTenantExportConfigMutation,
    CreateBigQueryTenantExportConfigMutationVariables
  >(CREATE_BIGQUERY_TENANT_EXPORT_CONFIG);

  async function handleSubmitCreate(
    settings: CreateBigQueryTenantExportConfigInput
  ) {
    const { errors } = await createExportConfig({
      variables: { input: settings },
    });

    if (errors) {
      throw new Error(getErrorMessage(errors));
    } else {
      setDialogOpen(false);
    }
  }

  const [updateExportConfig] = useMutation<
    UpdateBigQueryTenantExportConfigMutation,
    UpdateBigQueryTenantExportConfigMutationVariables
  >(UPDATE_BIGQUERY_TENANT_EXPORT_CONFIG);

  async function handleSubmitUpdate(
    settings: UpdateBigQueryTenantExportConfigInput
  ) {
    const { errors } = await updateExportConfig({
      variables: { input: settings },
    });

    if (errors) {
      throw new Error(getErrorMessage(errors));
    } else {
      setDialogOpen(false);
    }
  }

  const [deleteExportConfig] = useMutation<
    DeleteBigQueryTenantExportConfigMutation,
    DeleteBigQueryTenantExportConfigMutationVariables
  >(DELETE_BIGQUERY_TENANT_EXPORT_CONFIG, {
    onErrorSnackbarMessage: "Failed to disable BigQuery export",
  });

  function renderStatus() {
    if (!exportConfig) {
      return "Not configured";
    }

    if (!exportConfig.lastRunJob) {
      return "Has not run yet";
    }

    const startedAt = new Date(
      exportConfig.lastRunJob.startedAt
    ).toLocaleString();

    switch (exportConfig.lastRunJob.status) {
      case TrackedJobStatus.Created:
      case TrackedJobStatus.Queued:
      case TrackedJobStatus.Running:
        return `Running now (started at ${startedAt})`;
      case TrackedJobStatus.Done:
        return `Last run succeeded (started at ${startedAt})`;
      case TrackedJobStatus.Failed:
        return `Last run failed (started at ${startedAt}): ${exportConfig.lastRunJob.errorMessage}`;
      case TrackedJobStatus.Stalled:
        return `Last run failed (started at ${startedAt}): job stalled`;
    }
  }

  return (
    <Card>
      <CardHeader title="BigQuery Export" />
      <CardContent>
        <p>
          BigQuery Exports allow you to export all of your data to Google
          Cloud's BigQuery.
        </p>
        <p>
          <strong>Status:</strong> {renderStatus()}
        </p>
        <FlexBox flexDirection="row" justifyContent="space-between">
          <div>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="contained"
              color="primary"
              sx={{ marginRight: 1 }}
            >
              {exportConfig ? "Edit Configuration" : "Configure"}
            </Button>
            {exportConfig && (
              <BigQueryExportDeleteButton onDelete={deleteExportConfig} />
            )}
          </div>
          <div>
            <Button
              onClick={() => navigate("/organization/exports/dictionary")}
            >
              Data Dictionary
            </Button>
          </div>
        </FlexBox>

        {dialogOpen && (
          <BigQueryExportConfigDialog
            onClose={() => setDialogOpen(false)}
            exportConfig={exportConfig}
            handleSubmitCreate={handleSubmitCreate}
            handleSubmitUpdate={handleSubmitUpdate}
          />
        )}
      </CardContent>
    </Card>
  );
}
